import {Modal, StyleSheet, Text, TextInput, View} from "react-native";
import {Color} from "@/lib/styles";
import Page from "@/lib/components/Page";
import Center from "@/lib/components/Center";
import {useSession} from "@/lib/auth/auth";
import {AccountEditRequest} from "@/lib/types";
import {fetchBackendNoContent} from "@/lib/backend";
import ErrorPage from "@/lib/components/ErrorPage";
import SpinnerPage from "@/lib/components/SpinnerPage";
import Nav from "@/lib/components/Nav";
import {useMemo, useState} from "react";
import ErrorMessage from "@/lib/components/ErrorMessage";
import Editable from "@/lib/components/Editable";
import Colorpicker from "@/lib/components/Colorpicker";
import Split from "@/lib/components/Split";
import Avatar from "@/lib/components/Avatar";
import Icon from "@/lib/components/Icon";
import {useRouter} from "expo-router";
import {colorName} from "@/lib/color";
import ActionButton from "@/lib/components/ActionButton";
import Header from "@/lib/components/Header";
import {useAccount} from "@/lib/components/AccountProvider";
import PrimaryText from "@/lib/components/PrimaryText";
import globstyles from "@/lib/globstyles";

type EditableComponent = null | "color" | "gecos";

export default function Index() {

    const {session} = useSession();
    const router = useRouter();

    const [isEditing, setIsEditing] = useState<EditableComponent>(null);
    const [saveError, setSaveError] = useState<Error | null>(null);
    const [busy, setBusy] = useState(false);
    const [name, setName] = useState("");

    const {account, accountError, accountLoading, refreshAccount} = useAccount();

    const colorNameMemo = useMemo(() => {
        return colorName(account?.avatar.color!);
    }, [account])

    if (accountError) {
        return <ErrorPage error={accountError}/>
    } else if (accountLoading || !account) {
        return <SpinnerPage/>;
    }

    if (busy) return <SpinnerPage/>;

    const saveProfile = async (changes: AccountEditRequest) => {
        setSaveError(null);
        setBusy(true);
        fetchBackendNoContent(`/account`, "PATCH", session, changes).then(async () => {
            await refreshAccount();
            setIsEditing(null);
        }).catch((error) => {
            setSaveError(error);
        }).finally(() => {
            setIsEditing(null);
            setBusy(false);
        });
    }

    const cameraSize = 32;

    return (
        <Page scrollable={true}>
            <View style={[styles.card, {backgroundColor: account.avatar.color}]}>
                <Split left={
                    <>
                        <Avatar avatar={account.avatar} size={40}/>
                        <Text
                            selectable={false}
                            style={[styles.username, {color: account.avatar.contrast_color}]}
                        >
                            {account.avatar.gecos}
                        </Text>
                    </>
                } right={
                    <></>
                }/>
                <Editable
                    color={account.avatar.contrast_color}
                    style={{
                        flexDirection: "column",
                        alignItems: "center",
                        alignContent: "center",
                    }}
                    onPress={() => {
                        router.navigate("/u/account/picture");
                    }}
                >
                    <Avatar avatar={account.avatar} pressable={false} size={144} borderColor={account.avatar.contrast_color}/>
                    <View style={{
                        position: "absolute",
                        left: "34%",
                        top: "77%",
                        height: cameraSize,
                        width: cameraSize,
                        borderRadius: cameraSize,
                        backgroundColor: Color.Background,
                    }}>
                        <Icon kind={"camera"} color={Color.PrimaryText} size={cameraSize}/>
                    </View>
                </Editable>

                <Editable color={account.avatar.contrast_color} onPress={() => setIsEditing("color")}>
                    <Text style={[styles.editPart, {color: account.avatar.contrast_color}]}>Farge</Text>
                    <Text style={[styles.editValue, {color: account.avatar.contrast_color}]}>{colorNameMemo}</Text>
                </Editable>

                <Editable
                    color={account.avatar.contrast_color}
                    onPress={() => {
                        setName(account?.avatar.gecos);
                        setIsEditing("gecos");
                    }}>
                    <Text style={[styles.editPart, {color: account.avatar.contrast_color}]}>Navn</Text>
                    <Text
                        style={[styles.editValue, {color: account.avatar.contrast_color}]}>{account.avatar.gecos}</Text>
                </Editable>

            </View>

            {saveError && <ErrorMessage error={saveError}/>}

            <Nav>
                <ActionButton
                    title={"Ferdig"}
                    icon={"checkmark"}
                    onPress={async () => {
                        router.dismiss();
                    }}
                />
            </Nav>

            <Modal onRequestClose={() => setIsEditing(null)}
                   visible={!!isEditing}
                   allowSwipeDismissal={true}
                   presentationStyle={"pageSheet"}
                   animationType={"slide"}
            >
                <Page>
                    <Center>
                        {isEditing === "gecos" && <>
                            <Header title={"Skift navn"} center={true} marginBottom={16}/>
                            <PrimaryText>Bestem hvilket navn du vil bruke.</PrimaryText>
                            <TextInput style={globstyles.formTextInput}
                                       placeholder={"Bytt visningsnavn"}
                                       placeholderTextColor={Color.FadedText}
                                       value={name}
                                       onChangeText={(value) => {
                                           setName(value);
                                       }}
                                       inputMode={"text"}
                                       textContentType={"name"}
                                       autoCapitalize={"words"}
                                       returnKeyType={"send"}
                                       maxLength={64}
                                       enablesReturnKeyAutomatically={true}
                                       focusable={true}
                                       keyboardAppearance={"dark"}
                                       submitBehavior={"submit"}
                                       onSubmitEditing={async () => {
                                           await saveProfile({gecos: name});
                                       }}
                            />
                            <Split
                                left={
                                    <ActionButton
                                        title={"Tilbake"}
                                        onPress={() => setIsEditing(null)}
                                    />
                                }
                                right={
                                    <ActionButton
                                        title={"Lagre navn"}
                                        icon={"checkmark"}
                                        color={Color.Green}
                                        onPress={async () => {
                                            await saveProfile({gecos: name});
                                        }}
                                    />
                                }
                            />
                        </>}
                        {isEditing === "color" && <>
                            <Header title={"Velg profilfarge"} center={true} marginBottom={16}/>
                            <Colorpicker color={account.avatar.color} onChange={async (color) => {
                                setIsEditing(null);
                                await saveProfile({color: color.color});
                            }}/>
                        </>}
                    </Center>
                </Page>
            </Modal>
        </Page>
    );
}

const styles = StyleSheet.create({
    editPart: {
        fontSize: 16,
        fontWeight: 600,
    },
    editValue: {
        fontSize: 16,
    },
    card: {
        flexDirection: "column",
        borderRadius: 16,
        padding: 6,
        gap: 8,
    },
    username: {
        fontSize: 16,
        fontWeight: 600,
        flexShrink: 1,
    },
});